// ─── ProfitLens Theme — light & dark palettes ───────────────────────────────
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

const PALETTES = {
  dark: {
    bg: "#0d0f14",
    surface: "#111420",
    surfaceAlt: "#0d1018",
    border: "#1e2130",
    borderSoft: "#161820",
    inputBg: "#0d0f14",
    inputBorder: "#2a2d3e",
    headerBg: "rgba(13,15,20,0.95)",
    gridLine: "rgba(0,200,150,0.03)",
    text: "#e8e8f0",
    textStrong: "#fff",
    textBody: "#ccc",
    textMuted: "#aaa",
    textDim: "#778",
    textFaint: "#556",
    textGhost: "#445",
    textWhisper: "#334",
    primary: "#00c896",
    primaryDark: "#00896a",
    info: "#4fc3f7",
    warn: "#f5a623",
    danger: "#ff4d4d",
    dangerLight: "#ff6b6b",
    purple: "#e05ccc",
    indigo: "#8b7fe8",
    onPrimary: "#000",
    onWarn: "#000",
    onInfo: "#000",
    okBg: "#0f1e19",
    okBgStrong: "#001a11",
    okBorder: "#00c89633",
    okBorderStrong: "#00c89655",
    infoBg: "#001a2e",
    infoBorder: "#4fc3f733",
    warnBg: "#1a1000",
    warnBorder: "#f5a62333",
    dangerBg: "#1a0e0e",
    dangerBgSoft: "#ff4d4d22",
    dangerBorder: "#ff4d4d44",
    dangerLine: "#2a1515",
    badgePaidBg: "#002e1e",
    badgeDueBg: "#2e0000",
    badgePendingBg: "#1e1a00",
    incomeBg: "#003d2e",
    expenseBg: "#2d0a2d",
    salaryBg: "#2a1f5e",
    debtPaymentBg: "#1a1a2e",
    vendorCreditBg: "#3d1f00",
    btnGhost: "#1e2130",
    deleteBg: "#2a1515",
    footerBorder: "#141620",
    shadow: "0 4px 20px rgba(0,0,0,0.4)",
  },
  light: {
    bg: "#f5f7fb",
    surface: "#ffffff",
    surfaceAlt: "#f1f3f9",
    border: "#e3e6ee",
    borderSoft: "#eef0f6",
    inputBg: "#ffffff",
    inputBorder: "#d4d8e3",
    headerBg: "rgba(255,255,255,0.92)",
    gridLine: "rgba(0,168,128,0.05)",
    text: "#1a1d2b",
    textStrong: "#0a0c14",
    textBody: "#2c3142",
    textMuted: "#4a4f63",
    textDim: "#6b6f80",
    textFaint: "#8b8f9f",
    textGhost: "#a8abbb",
    textWhisper: "#c1c4d0",
    primary: "#00a880",
    primaryDark: "#007a5c",
    info: "#0ea5e9",
    warn: "#d97706",
    danger: "#e11d48",
    dangerLight: "#dc2626",
    purple: "#c026d3",
    indigo: "#6366f1",
    onPrimary: "#ffffff",
    onWarn: "#ffffff",
    onInfo: "#ffffff",
    okBg: "#e6f7f1",
    okBgStrong: "#d1f0e3",
    okBorder: "#00a88044",
    okBorderStrong: "#00a88066",
    infoBg: "#e0f2fe",
    infoBorder: "#0ea5e966",
    warnBg: "#fef3c7",
    warnBorder: "#d9770655",
    dangerBg: "#fef2f2",
    dangerBgSoft: "#fee2e2",
    dangerBorder: "#e11d4855",
    dangerLine: "#fecaca",
    badgePaidBg: "#d1fae5",
    badgeDueBg: "#fee2e2",
    badgePendingBg: "#fef3c7",
    incomeBg: "#d1fae5",
    expenseBg: "#fae8ff",
    salaryBg: "#e0e7ff",
    debtPaymentBg: "#dbeafe",
    vendorCreditBg: "#ffedd5",
    btnGhost: "#eef0f6",
    deleteBg: "#fee2e2",
    footerBorder: "#e3e6ee",
    shadow: "0 4px 20px rgba(20,30,60,0.10)",
  },
};

export function createTheme(mode = "dark") {
  const c = PALETTES[mode] ? PALETTES[mode] : PALETTES.dark;
  return {
    mode,
    colors: c,

    root: {
      minHeight: "100vh",
      background: c.bg,
      color: c.text,
      fontFamily: "'DM Sans','Segoe UI',sans-serif",
      position: "relative",
      overflowX: "hidden",
    },
    bgGrid: {
      position: "fixed",
      inset: 0,
      backgroundImage: `linear-gradient(${c.gridLine} 1px,transparent 1px),linear-gradient(90deg,${c.gridLine} 1px,transparent 1px)`,
      backgroundSize: "40px 40px",
      pointerEvents: "none",
      zIndex: 0,
    },
    toast: {
      position: "fixed",
      top: 20,
      right: 20,
      padding: "12px 24px",
      borderRadius: 8,
      color: "#fff",
      fontWeight: 600,
      fontSize: 14,
      zIndex: 999,
      boxShadow: c.shadow,
    },
    header: {
      position: "relative",
      zIndex: 1,
      borderBottom: `1px solid ${c.border}`,
      background: c.headerBg,
      backdropFilter: "blur(10px)",
      padding: "0 24px",
    },
    headerInner: {
      maxWidth: 1100,
      margin: "0 auto",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: "16px 0",
    },
    logo: { display: "flex", alignItems: "center", gap: 10 },
    logoIcon: {
      width: 38,
      height: 38,
      background: `linear-gradient(135deg,${c.primary},${c.primaryDark})`,
      borderRadius: 10,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: 20,
      fontWeight: 800,
      color: "#fff",
    },
    logoText: {
      fontSize: 22,
      fontWeight: 800,
      letterSpacing: "-0.5px",
      color: c.textStrong,
    },
    headerSub: {
      fontSize: 10,
      color: c.textGhost,
      letterSpacing: "0.8px",
      textTransform: "uppercase",
      marginTop: 2,
      paddingLeft: 48,
    },
    headerDate: { textAlign: "right" },
    dateLabel: {
      fontSize: 10,
      color: c.textFaint,
      textTransform: "uppercase",
      letterSpacing: "1px",
    },
    dateValue: { fontSize: 16, fontWeight: 700, color: c.textBody },
    alertBadge: {
      marginTop: 4,
      background: c.dangerBgSoft,
      color: c.dangerLight,
      border: `1px solid ${c.dangerBorder}`,
      borderRadius: 4,
      fontSize: 11,
      fontWeight: 600,
      padding: "2px 8px",
    },
    nav: {
      position: "relative",
      zIndex: 1,
      display: "flex",
      gap: 4,
      padding: "10px 24px",
      maxWidth: 1148,
      margin: "0 auto",
      overflowX: "auto",
    },
    navBtn: {
      background: "transparent",
      border: `1px solid ${c.border}`,
      color: c.textDim,
      borderRadius: 8,
      padding: "7px 14px",
      fontSize: 12,
      fontWeight: 600,
      cursor: "pointer",
      whiteSpace: "nowrap",
      position: "relative",
    },
    navActive: {
      background: c.okBg,
      border: `1px solid ${c.okBorder}`,
      color: c.primary,
    },
    navBadge: {
      background: c.danger,
      color: "#fff",
      borderRadius: 10,
      fontSize: 10,
      fontWeight: 800,
      padding: "1px 5px",
      marginLeft: 5,
    },
    main: {
      position: "relative",
      zIndex: 1,
      maxWidth: 1100,
      margin: "0 auto",
      padding: "20px 24px 60px",
    },

    // Opening balance banner
    obBanner: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      background: c.infoBg,
      border: `1px solid ${c.infoBorder}`,
      borderRadius: 14,
      padding: "16px 20px",
      marginBottom: 24,
      gap: 16,
      flexWrap: "wrap",
    },
    obBannerLeft: { display: "flex", alignItems: "center", gap: 14 },
    obBannerRight: { display: "flex", alignItems: "center", gap: 10 },
    obBannerBtn: {
      background: c.info,
      color: c.onInfo,
      border: "none",
      borderRadius: 8,
      padding: "10px 20px",
      fontSize: 13,
      fontWeight: 800,
      cursor: "pointer",
      whiteSpace: "nowrap",
    },

    // Cash flow panel
    cashFlowPanel: {
      display: "grid",
      gridTemplateColumns: "1fr 320px",
      gap: 16,
      marginBottom: 28,
    },
    cashFlowLeft: {
      background: c.surface,
      border: `1px solid ${c.border}`,
      borderRadius: 14,
      padding: "20px 24px",
    },
    cashFlowRight: { display: "flex", flexDirection: "column", gap: 12 },
    flowRow: { display: "flex", alignItems: "center", gap: 10, padding: "7px 0" },
    flowIcon: { fontSize: 18, width: 26, textAlign: "center" },
    flowLabel: { flex: 1, color: c.textMuted, fontSize: 14 },
    flowSub: { fontSize: 11, color: c.textGhost, marginRight: 8 },
    flowValue: { fontWeight: 700, fontSize: 15, whiteSpace: "nowrap" },
    flowDivider: { height: 1, background: c.borderSoft, margin: "4px 0" },
    eodBox: {
      background: c.okBgStrong,
      border: `1px solid ${c.okBorder}`,
      borderRadius: 12,
      padding: "16px 18px",
    },
    eodPendingBox: {
      background: c.warnBg,
      border: `1px solid ${c.warnBorder}`,
      borderRadius: 12,
      padding: "14px 18px",
    },
    eodStatusBox: {
      background: c.surface,
      border: `1px solid ${c.border}`,
      borderRadius: 12,
      padding: "14px 18px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    eodLabel: {
      fontSize: 11,
      color: c.textFaint,
      textTransform: "uppercase",
      letterSpacing: "0.8px",
      marginBottom: 6,
    },
    eodValue: { fontSize: 28, fontWeight: 800, letterSpacing: "-1px" },
    eodFormula: { fontSize: 11, color: c.textGhost, marginTop: 4 },

    // Stat cards
    sectionTitle: {
      fontSize: 12,
      fontWeight: 700,
      color: c.textFaint,
      letterSpacing: "1.5px",
      textTransform: "uppercase",
      marginBottom: 14,
      marginTop: 0,
    },
    tabDesc: { color: c.textFaint, fontSize: 13, marginBottom: 18, marginTop: -8 },
    cardGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fill,minmax(190px,1fr))",
      gap: 12,
    },
    statCard: {
      background: c.surface,
      border: `1px solid ${c.border}`,
      borderRadius: 14,
      padding: "16px 18px",
    },
    statCardBig: { gridColumn: "span 2" },
    statIcon: { fontSize: 20, marginBottom: 7 },
    statValue: { fontSize: 20, fontWeight: 800, letterSpacing: "-0.5px" },
    statLabel: { fontSize: 11, color: c.textFaint, marginTop: 3, fontWeight: 500 },
    statSub: { fontSize: 10, color: c.textGhost, marginTop: 4 },

    // Daily view
    dateNavRow: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      marginBottom: 20,
    },
    dateNavBtn: {
      background: c.surface,
      border: `1px solid ${c.border}`,
      color: c.textMuted,
      borderRadius: 8,
      padding: "8px 14px",
      fontSize: 13,
      cursor: "pointer",
      fontWeight: 600,
    },
    txList: { display: "flex", flexDirection: "column", gap: 8 },
    txRow: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      background: c.surface,
      border: `1px solid ${c.border}`,
      borderRadius: 8,
      padding: "10px 14px",
    },
    txLabel: { flex: 1, fontSize: 13, color: c.textBody },
    txAmt: { fontWeight: 700, fontSize: 14, whiteSpace: "nowrap" },

    // Categories
    categoryGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fill,minmax(170px,1fr))",
      gap: 10,
    },
    catCard: {
      background: c.surface,
      border: `1px solid ${c.border}`,
      borderRadius: 12,
      padding: "12px 14px",
    },
    catName: { fontSize: 11, color: c.textDim, fontWeight: 600, marginBottom: 5 },
    catAmount: { fontSize: 17, fontWeight: 800, marginBottom: 7 },
    catBar: {
      height: 4,
      background: c.border,
      borderRadius: 2,
      marginBottom: 5,
      overflow: "hidden",
    },
    catBarFill: { height: "100%", background: c.purple, borderRadius: 2 },
    catPct: { fontSize: 10, color: c.textGhost },

    // Alerts
    alertBox: {
      background: c.dangerBg,
      border: `1px solid ${c.dangerBorder}`,
      borderRadius: 14,
      padding: "16px 20px",
    },
    alertTitle: {
      color: c.dangerLight,
      fontWeight: 700,
      fontSize: 14,
      marginBottom: 12,
    },
    alertRow: {
      display: "flex",
      alignItems: "center",
      gap: 12,
      padding: "8px 0",
      borderTop: `1px solid ${c.dangerLine}`,
      fontSize: 13,
      flexWrap: "wrap",
    },
    alertMeta: { color: c.textDim, fontSize: 12, marginLeft: "auto" },
    payBtn: {
      background: c.primary,
      color: c.onPrimary,
      border: "none",
      borderRadius: 6,
      padding: "5px 12px",
      fontSize: 12,
      fontWeight: 700,
      cursor: "pointer",
    },

    // Forms
    formCard: {
      background: c.surface,
      border: `1px solid ${c.border}`,
      borderRadius: 14,
      padding: 22,
    },
    formGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fill,minmax(210px,1fr))",
      gap: 14,
    },
    formField: { display: "flex", flexDirection: "column", gap: 5 },
    label: {
      fontSize: 10,
      color: c.textFaint,
      fontWeight: 600,
      letterSpacing: "0.8px",
      textTransform: "uppercase",
    },
    input: {
      background: c.inputBg,
      border: `1px solid ${c.inputBorder}`,
      borderRadius: 8,
      padding: "10px 14px",
      color: c.text,
      fontSize: 14,
      outline: "none",
      width: "100%",
      boxSizing: "border-box",
    },
    creditNote: {
      marginTop: 12,
      fontSize: 12,
      color: c.textDim,
      background: c.surfaceAlt,
      borderRadius: 6,
      padding: "8px 12px",
      borderLeft: `3px solid ${c.inputBorder}`,
    },
    submitBtn: {
      marginTop: 16,
      background: c.warn,
      color: c.onWarn,
      border: "none",
      borderRadius: 8,
      padding: "11px 26px",
      fontSize: 14,
      fontWeight: 800,
      cursor: "pointer",
    },

    // Table
    tableWrap: {
      overflowX: "auto",
      borderRadius: 12,
      border: `1px solid ${c.border}`,
    },
    table: { width: "100%", borderCollapse: "collapse", fontSize: 13 },
    th: {
      background: c.surface,
      color: c.textFaint,
      fontWeight: 600,
      fontSize: 10,
      letterSpacing: "0.8px",
      textTransform: "uppercase",
      padding: "11px 14px",
      textAlign: "left",
      borderBottom: `1px solid ${c.border}`,
    },
    tr: { borderBottom: `1px solid ${c.borderSoft}` },
    td: { padding: "10px 14px", color: c.textBody, verticalAlign: "middle" },
    emptyCell: {
      textAlign: "center",
      color: c.textGhost,
      padding: "32px 0",
      fontSize: 13,
    },
    badgePaid: {
      background: c.badgePaidBg,
      color: c.primary,
      borderRadius: 4,
      padding: "3px 8px",
      fontSize: 11,
      fontWeight: 700,
    },
    badgeDue: {
      background: c.badgeDueBg,
      color: c.dangerLight,
      borderRadius: 4,
      padding: "3px 8px",
      fontSize: 11,
      fontWeight: 700,
    },
    badgePending: {
      background: c.badgePendingBg,
      color: c.warn,
      borderRadius: 4,
      padding: "3px 8px",
      fontSize: 11,
      fontWeight: 700,
    },
    smallBtn: {
      background: c.primary,
      color: c.onPrimary,
      border: "none",
      borderRadius: 5,
      padding: "4px 10px",
      fontSize: 11,
      fontWeight: 700,
      cursor: "pointer",
      marginRight: 4,
    },
    deleteBtn: { background: c.deleteBg, color: c.dangerLight },
    typeBadge: {
      borderRadius: 4,
      padding: "3px 8px",
      fontSize: 11,
      fontWeight: 700,
    },
    ledgerFooter: {
      display: "flex",
      justifyContent: "flex-end",
      gap: 28,
      marginTop: 14,
      padding: "12px 18px",
      background: c.surface,
      borderRadius: 10,
      fontSize: 13,
    },
    ledgerTotal: { display: "flex", gap: 8, alignItems: "center" },
    clearBtn: {
      background: c.btnGhost,
      color: c.textMuted,
      border: "none",
      borderRadius: 6,
      padding: "7px 14px",
      fontSize: 12,
      cursor: "pointer",
      fontWeight: 600,
    },
    footer: {
      position: "relative",
      zIndex: 1,
      textAlign: "center",
      padding: "18px 0",
      color: c.textWhisper,
      fontSize: 11,
      letterSpacing: "0.5px",
      borderTop: `1px solid ${c.footerBorder}`,
    },

    // Debt manager
    debtSummaryStrip: {
      display: "flex",
      alignItems: "center",
      background: c.surface,
      border: `1px solid ${c.border}`,
      borderRadius: 14,
      padding: "16px 24px",
      gap: 0,
      flexWrap: "wrap",
    },
    debtSummaryItem: { flex: 1, textAlign: "center", minWidth: 110 },
    debtSummaryDivider: { width: 1, height: 36, background: c.border },
    debtSummaryValue: { fontSize: 22, fontWeight: 800, color: c.text },
    debtSummaryLabel: {
      fontSize: 10,
      color: c.textFaint,
      marginTop: 3,
      textTransform: "uppercase",
      letterSpacing: "0.8px",
    },
    cashInputRow: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      background: c.surface,
      border: `1px solid ${c.border}`,
      borderRadius: 12,
      padding: "14px 18px",
      marginBottom: 14,
      gap: 14,
      flexWrap: "wrap",
    },
    cashInputLabel: { display: "flex", alignItems: "center", gap: 12 },
    strategyRow: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      flexWrap: "wrap",
      marginBottom: 10,
    },
    strategyLabel: { fontSize: 12, color: c.textFaint, fontWeight: 600 },
    strategyBtn: {
      background: c.surface,
      border: `1px solid ${c.border}`,
      color: c.textDim,
      borderRadius: 8,
      padding: "6px 13px",
      fontSize: 12,
      fontWeight: 600,
      cursor: "pointer",
    },
    strategyActive: {
      background: c.okBg,
      border: `1px solid ${c.okBorderStrong}`,
      color: c.primary,
    },
    strategyExplain: {
      background: c.surfaceAlt,
      border: `1px solid ${c.border}`,
      borderRadius: 8,
      padding: "9px 13px",
      fontSize: 12,
      color: c.textDim,
      marginBottom: 18,
      borderLeft: `3px solid ${c.primary}`,
    },
    debtCardList: { display: "flex", flexDirection: "column", gap: 14 },
    debtCard: {
      background: c.surface,
      border: `1px solid ${c.border}`,
      borderRadius: 16,
      padding: 18,
    },
    debtCardHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: 8,
    },
    priorityBadge: {
      borderRadius: 4,
      padding: "3px 8px",
      fontSize: 10,
      fontWeight: 800,
      letterSpacing: "0.5px",
    },
    debtVendorName: { fontSize: 15, fontWeight: 700, color: c.text },
    overdueBadge: {
      background: c.dangerBgSoft,
      color: c.danger,
      border: `1px solid ${c.dangerBorder}`,
      borderRadius: 4,
      fontSize: 10,
      fontWeight: 800,
      padding: "2px 7px",
    },
    debtNote: { fontSize: 12, color: c.textFaint, marginBottom: 9 },
    debtProgressRow: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      marginBottom: 12,
    },
    debtProgressBar: {
      flex: 1,
      height: 5,
      background: c.border,
      borderRadius: 3,
      overflow: "hidden",
    },
    debtProgressFill: { height: "100%", borderRadius: 3 },
    debtPct: { fontSize: 11, color: c.textFaint, whiteSpace: "nowrap" },
    debtAmountRow: {
      display: "flex",
      gap: 20,
      flexWrap: "wrap",
      marginBottom: 12,
    },
    debtAmountLabel: {
      fontSize: 10,
      color: c.textFaint,
      textTransform: "uppercase",
      letterSpacing: "0.8px",
      marginBottom: 2,
    },
    debtAmountValue: { fontSize: 14, fontWeight: 700, color: c.textBody },
    suggestionBox: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      padding: "9px 13px",
      borderRadius: 8,
      border: "1px solid",
      marginBottom: 12,
      fontSize: 13,
    },
    debtPayRow: {
      display: "flex",
      alignItems: "center",
      gap: 6,
      flexWrap: "wrap",
    },
    paymentHistory: {
      marginTop: 12,
      borderTop: `1px solid ${c.border}`,
      paddingTop: 10,
    },
    paymentHistoryTitle: {
      fontSize: 10,
      color: c.textGhost,
      textTransform: "uppercase",
      letterSpacing: "1px",
      marginBottom: 7,
    },
    paymentHistoryRow: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      fontSize: 12,
      padding: "4px 0",
      borderBottom: `1px solid ${c.borderSoft}`,
    },
    emptyDebt: { textAlign: "center", padding: "60px 0", color: c.textGhost },

    // Settings tab
    settingsSummary: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fill,minmax(130px,1fr))",
      gap: 10,
      marginBottom: 8,
    },
    settingsSummaryCard: {
      background: c.surface,
      border: `1px solid ${c.border}`,
      borderRadius: 12,
      padding: "14px 16px",
      textAlign: "center",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: 4,
    },
    settingsCard: {
      background: c.surface,
      border: `1px solid ${c.border}`,
      borderRadius: 14,
      padding: "20px",
      display: "flex",
      gap: 18,
      alignItems: "flex-start",
    },
    settingsCardIcon: {
      fontSize: 28,
      minWidth: 36,
      textAlign: "center",
      paddingTop: 2,
    },
    settingsCardBody: { flex: 1 },
    settingsCardTitle: {
      fontSize: 15,
      fontWeight: 700,
      color: c.text,
      marginBottom: 6,
    },
    settingsCardDesc: { fontSize: 13, color: c.textDim, lineHeight: 1.6 },
    fileLabel: {
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      marginTop: 14,
      background: c.surfaceAlt,
      border: `1px solid ${c.inputBorder}`,
      borderRadius: 8,
      padding: "10px 18px",
      fontSize: 13,
      color: c.textMuted,
      cursor: "pointer",
      fontWeight: 600,
    },
    importStatus: {
      marginTop: 16,
      padding: "12px 16px",
      borderRadius: 10,
      border: "1px solid",
      fontSize: 13,
      fontWeight: 600,
    },
    code: {
      background: c.surfaceAlt,
      border: `1px solid ${c.inputBorder}`,
      borderRadius: 4,
      padding: "1px 6px",
      fontSize: 12,
      color: c.info,
      fontFamily: "monospace",
    },
  };
}

// ─── React context ──────────────────────────────────────────────────────────
const ThemeContext = createContext(null);
const STORAGE_KEY = "profitlens.themeMode";

function readInitialMode() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === "light" || saved === "dark") return saved;
  } catch {}
  if (typeof window !== "undefined" && window.matchMedia) {
    return window.matchMedia("(prefers-color-scheme: light)").matches
      ? "light"
      : "dark";
  }
  return "dark";
}

export function ThemeProvider({ children }) {
  const [mode, setMode] = useState(readInitialMode);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, mode);
    } catch {}
  }, [mode]);

  const theme = useMemo(() => createTheme(mode), [mode]);

  const value = useMemo(
    () => ({
      mode,
      setMode,
      toggleMode: () => setMode((m) => (m === "dark" ? "light" : "dark")),
      theme,
    }),
    [mode, theme],
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  return ctx ? ctx.theme : createTheme("dark");
}

export function useThemeMode() {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    return {
      mode: "dark",
      setMode: () => {},
      toggleMode: () => {},
    };
  }
  return ctx;
}

// Default export kept for any straggling imports — resolves to dark theme.
const S = createTheme("dark");
export default S;
